<template>
  <div class="som-tv-root">
    <header class="som-tv-header">
      <h1 class="som-tv-title">Select-o-Matic</h1>
      <p v-if="!selectedLocation" class="som-tv-sub text-warning">
        Add <code class="som-tv-code">?location=</code> plus your venue ID to this page’s URL (open from the host dashboard).
      </p>
      <p v-else-if="loadError" class="som-tv-sub text-danger">{{ loadError }}</p>
      <p v-else-if="!gameId" class="som-tv-sub som-tv-muted">
        No game scheduled for this location today (Eastern time).
      </p>
      <template v-else>
        <p class="som-tv-sub som-tv-muted">
          {{ locationLabel }} · {{ sessionLine }}
        </p>
        <div
          v-if="showSuitLegend"
          class="som-tv-legend-row"
          role="group"
          aria-label="If two cards tie on rank, suits break the tie in this order, strongest first: Spades, Hearts, Diamonds, Clubs"
        >
          <span class="som-tv-legend-prefix">High card wins · Same rank:</span>
          <div class="som-tv-suit-chain">
            <template v-for="(item, idx) in suitTiebreakDisplay" :key="'tie-' + idx">
              <span v-if="idx > 0" class="som-tv-gt" aria-hidden="true">&gt;</span>
              <span class="som-tv-legend-suit" :class="{ 'som-suit-red': item.red }">{{ item.symbol }}</span>
            </template>
          </div>
        </div>
      </template>
    </header>

    <main v-if="selectedLocation && gameId" class="som-tv-main">
      <transition-group name="som-tv-tile" tag="div" class="som-tv-grid">
        <article
          v-for="row in displayRows"
          :key="row.teamId"
          class="som-tv-tile"
          :class="tileClass(row)"
        >
          <div class="som-tv-team-block">
            <div class="som-tv-team-name">{{ row.teamName }}</div>
            <div class="som-tv-wins-line" :class="{ 'som-tv-wins-highlight': row.somWins > 0 }">
              Wins: {{ row.somWins }}
            </div>
          </div>
          <div class="som-tv-body">
            <template v-if="row.state === 'locked'">
              <div
                class="som-tv-card-face"
                :class="{ red: cardIdIsRed(row.chosenCard) }"
              >
                <span class="som-tv-rank">{{ rankPart(row.chosenCard) }}</span>
                <span class="som-tv-suit">{{ symbolPartLocal(row.chosenCard) }}</span>
              </div>
              <div class="som-tv-card-caption">{{ cardIdToShortLabel(row.chosenCard) }}</div>
            </template>
            <template v-else-if="row.state === 'declined'">
              <div class="som-tv-optout" aria-label="Did not enter">
                <span class="som-tv-x">✗</span>
              </div>
              <div class="som-tv-card-caption text-muted">Not in this draw</div>
            </template>
            <template v-else-if="row.state === 'choosing'">
              <div class="som-tv-pending choosing">
                <span class="som-tv-dots" aria-hidden="true">…</span>
              </div>
              <div class="som-tv-card-caption text-muted">Choosing…</div>
            </template>
            <template v-else-if="row.state === 'pending'">
              <div class="som-tv-pending">
                <span class="som-tv-hourglass" aria-hidden="true">⏳</span>
              </div>
              <div class="som-tv-card-caption text-muted">Waiting…</div>
            </template>
            <template v-else>
              <div class="som-tv-pending idle">
                <span>—</span>
              </div>
              <div class="som-tv-card-caption text-muted">No draw yet</div>
            </template>
          </div>
        </article>
      </transition-group>
    </main>
  </div>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import { onSnapshot, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/firebase';
import {
  sessionDocRef,
  selectOMaticParticipantsCollection,
  selectOMaticWinsCollection,
} from '@/services/selectOMaticService';
import {
  cardIdToShortLabel,
  cardIdIsRed,
  compareCardIds,
  RANK_SHORT,
} from '@/utils/playingCards';
import moment from 'moment-timezone';

/** Strongest → weakest (standard high-card tiebreak). */
const suitTiebreakDisplay = [
  { symbol: '♠', red: false },
  { symbol: '♥', red: true },
  { symbol: '♦', red: true },
  { symbol: '♣', red: false },
];

export default {
  name: 'SelectOMaticTvDisplay',
  props: {
    selectedLocation: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const gameId = ref('');
    const locationName = ref('');
    const sessionActive = ref(false);
    const liveSessionId = ref('');
    const teams = ref([]);
    const participants = ref([]);
    const winsByTeam = ref({});
    const loadError = ref('');
    let unsubSession = null;
    let unsubParticipants = null;
    let unsubTeams = null;
    let unsubWins = null;

    function getTodayET() {
      return moment.tz('America/New_York').format('YYYY-MM-DD');
    }

    function symbolPartLocal(id) {
      if (id == null) return '';
      const s = Math.floor(Number(id) / 13);
      return ['♣', '♦', '♥', '♠'][s];
    }

    function rankPart(id) {
      if (id == null) return '';
      return RANK_SHORT[Number(id) % 13];
    }

    const locationLabel = computed(() => locationName.value || props.selectedLocation || 'Venue');

    const showSuitLegend = computed(
      () => sessionActive.value && !!liveSessionId.value
    );

    const sessionLine = computed(() => {
      if (!sessionActive.value || !liveSessionId.value) {
        return 'Session idle — rankings appear when the host starts Select-o-Matic.';
      }
      return 'Live draw';
    });

    const displayRows = computed(() => {
      const sid = liveSessionId.value;
      const active = sessionActive.value;

      const byTeamId = new Map();
      participants.value.forEach((p) => {
        if (p.teamId != null) {
          byTeamId.set(String(p.teamId), p);
        }
      });

      const rows = teams.value.map((t) => {
        const teamId = String(t.teamId ?? t.id);
        const part = byTeamId.get(teamId);
        const match = !!(part && sid && part.sessionId === sid);

        let state = 'idle';
        let chosenCard = null;

        if (active && sid) {
          if (!match) {
            state = 'pending';
          } else if (part.declined) {
            state = 'declined';
          } else if (part.chosenCard != null && part.chosenCard !== '') {
            state = 'locked';
            chosenCard = Number(part.chosenCard);
          } else if (Array.isArray(part.cards) && part.cards.length === 2) {
            state = 'choosing';
          } else {
            state = 'pending';
          }
        }

        const somWins = winsByTeam.value[teamId] || 0;

        return {
          teamId,
          teamName: t.teamName || `Team ${teamId}`,
          state,
          chosenCard,
          somWins,
        };
      });

      const locked = rows.filter((r) => r.state === 'locked').sort((a, b) => compareCardIds(b.chosenCard, a.chosenCard));
      const choosing = rows.filter((r) => r.state === 'choosing').sort((a, b) => a.teamName.localeCompare(b.teamName));
      const pending = rows.filter((r) => r.state === 'pending').sort((a, b) => a.teamName.localeCompare(b.teamName));
      const declined = rows.filter((r) => r.state === 'declined').sort((a, b) => a.teamName.localeCompare(b.teamName));
      const idle = rows.filter((r) => r.state === 'idle').sort((a, b) => a.teamName.localeCompare(b.teamName));

      return [...locked, ...choosing, ...pending, ...declined, ...idle];
    });

    function tileClass(row) {
      return {
        'is-locked': row.state === 'locked',
        'is-declined': row.state === 'declined',
        'is-choosing': row.state === 'choosing',
        'is-pending': row.state === 'pending',
        'is-idle': row.state === 'idle',
      };
    }

    function detachListeners() {
      if (unsubSession) {
        unsubSession();
        unsubSession = null;
      }
      if (unsubParticipants) {
        unsubParticipants();
        unsubParticipants = null;
      }
      if (unsubTeams) {
        unsubTeams();
        unsubTeams = null;
      }
      if (unsubWins) {
        unsubWins();
        unsubWins = null;
      }
    }

    async function resolveGameAndLocation() {
      gameId.value = '';
      teams.value = [];
      participants.value = [];
      winsByTeam.value = {};
      sessionActive.value = false;
      liveSessionId.value = '';
      loadError.value = '';
      detachListeners();

      if (!props.selectedLocation) return;

      try {
        const today = getTodayET();
        const gamesQ = query(
          collection(db, 'games'),
          where('location', '==', props.selectedLocation),
          where('date', '==', today)
        );
        const gamesSnap = await getDocs(gamesQ);
        if (gamesSnap.empty) {
          return;
        }
        const gid = gamesSnap.docs[0].id;
        gameId.value = gid;

        const locSnap = await getDocs(collection(db, 'locations'));
        const loc = locSnap.docs.find((d) => d.id === props.selectedLocation);
        if (loc) {
          locationName.value = loc.data().name || '';
        }

        const teamsQ = query(
          collection(db, 'teams'),
          where('location', '==', props.selectedLocation),
          where('gameId', '==', gid)
        );
        unsubTeams = onSnapshot(
          teamsQ,
          (snap) => {
            teams.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          },
          (err) => {
            console.error(err);
            loadError.value = err.message || 'Could not load teams.';
          }
        );

        const sRef = sessionDocRef(db, gid);
        unsubSession = onSnapshot(sRef, (snap) => {
          if (!snap.exists()) {
            sessionActive.value = false;
            liveSessionId.value = '';
            return;
          }
          const d = snap.data();
          sessionActive.value = !!d.active;
          liveSessionId.value = d.sessionId || '';
        });

        const partsRef = selectOMaticParticipantsCollection(db, gid);
        unsubParticipants = onSnapshot(partsRef, (snap) => {
          participants.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        });

        const winsRef = selectOMaticWinsCollection(db, gid);
        unsubWins = onSnapshot(winsRef, (snap) => {
          const m = {};
          snap.docs.forEach((d) => {
            m[d.id] = Number(d.data().wins || 0);
          });
          winsByTeam.value = m;
        });
      } catch (e) {
        console.error(e);
        loadError.value = e.message || 'Failed to load game.';
      }
    }

    watch(
      () => props.selectedLocation,
      () => {
        resolveGameAndLocation();
      },
      { immediate: true }
    );

    onUnmounted(() => detachListeners());

    return {
      gameId,
      loadError,
      locationLabel,
      sessionLine,
      showSuitLegend,
      suitTiebreakDisplay,
      displayRows,
      tileClass,
      cardIdToShortLabel,
      cardIdIsRed,
      rankPart,
      symbolPartLocal,
    };
  },
};
</script>

<style scoped>
.som-tv-root {
  min-height: 100vh;
  background: linear-gradient(165deg, #1e1b4b 0%, #312e81 40%, #4c1d95 100%);
  color: #f5f3ff;
  padding: clamp(1rem, 3vw, 2.5rem);
  box-sizing: border-box;
}

.som-tv-header {
  text-align: center;
  margin-bottom: clamp(1rem, 2.5vw, 2rem);
}

.som-tv-title {
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-weight: 800;
  font-size: clamp(1.75rem, 5vw, 3.25rem);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 0 0 0.5rem;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.4);
}

.som-tv-sub {
  margin: 0;
  font-size: clamp(0.95rem, 2.2vw, 1.35rem);
  opacity: 0.95;
}

.som-tv-muted {
  opacity: 0.85;
}

.som-tv-legend-row {
  margin: 0.75rem auto 0;
  max-width: 42rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem 0.85rem;
  opacity: 0.92;
  font-size: clamp(0.85rem, 2vw, 1.1rem);
}

.som-tv-legend-prefix {
  font-weight: 600;
  color: rgba(245, 243, 255, 0.88);
}

.som-tv-suit-chain {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.15rem 0.35rem;
}

.som-tv-gt {
  font-weight: 800;
  font-size: 1.15em;
  color: #fde047;
  margin: 0 0.1rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.som-tv-legend-suit {
  font-size: clamp(1.75rem, 5vw, 2.75rem);
  line-height: 1;
  font-family: 'Segoe UI', 'Apple Color Emoji', 'Noto Color Emoji', serif;
  color: #e9e9f0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}

.som-tv-legend-suit.som-suit-red {
  color: #fb7185;
}

.som-tv-code {
  background: rgba(0, 0, 0, 0.25);
  padding: 0.1em 0.35em;
  border-radius: 4px;
}

.som-tv-main {
  max-width: 1600px;
  margin: 0 auto;
}

.som-tv-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr));
  gap: clamp(0.75rem, 2vw, 1.25rem);
}

.som-tv-tile {
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 200px;
  transition:
    transform 0.35s ease,
    box-shadow 0.35s ease,
    border-color 0.2s ease;
}

.som-tv-tile.is-locked {
  border-color: #a78bfa;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
}

.som-tv-tile.is-declined {
  border-color: rgba(239, 68, 68, 0.65);
}

.som-tv-team-block {
  text-align: center;
  margin-bottom: 0.75rem;
  width: 100%;
}

.som-tv-team-name {
  font-weight: 700;
  font-size: clamp(1rem, 2.5vw, 1.35rem);
  line-height: 1.2;
}

.som-tv-wins-line {
  font-size: clamp(0.8rem, 2vw, 1.05rem);
  font-weight: 600;
  margin-top: 0.25rem;
  opacity: 0.75;
}

.som-tv-wins-line.som-tv-wins-highlight {
  color: #fde047;
  opacity: 1;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
}

.som-tv-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.som-tv-card-face {
  width: 100%;
  max-width: 140px;
  aspect-ratio: 5 / 7;
  background: linear-gradient(160deg, #fff 0%, #f3e8ff 100%);
  border: 3px solid #7c3aed;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #1e1b4b;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
}

.som-tv-card-face.red {
  color: #b91c1c;
}

.som-tv-rank {
  font-size: clamp(2rem, 5vw, 2.75rem);
  font-weight: 800;
  line-height: 1;
}

.som-tv-suit {
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  line-height: 1;
  margin-top: 0.35rem;
}

.som-tv-card-caption {
  margin-top: 0.5rem;
  font-size: clamp(0.8rem, 1.8vw, 1rem);
  text-align: center;
  font-weight: 600;
}

.som-tv-optout {
  width: 100%;
  max-width: 120px;
  aspect-ratio: 1;
  border-radius: 16px;
  background: rgba(127, 29, 29, 0.35);
  border: 3px dashed rgba(248, 113, 113, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

.som-tv-x {
  font-size: clamp(3rem, 10vw, 4.5rem);
  font-weight: 900;
  color: #fca5a5;
  line-height: 1;
}

.som-tv-pending {
  width: 100%;
  max-width: 120px;
  aspect-ratio: 5 / 7;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 2px dashed rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  opacity: 0.85;
}

.som-tv-pending.choosing .som-tv-dots {
  animation: som-blink 1.2s ease-in-out infinite;
}

.som-tv-pending.idle {
  font-size: 1.75rem;
  opacity: 0.5;
}

@keyframes som-blink {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}

/* TransitionGroup move */
.som-tv-tile-move {
  transition: transform 0.45s ease;
}

.som-tv-tile-enter-active,
.som-tv-tile-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.som-tv-tile-enter-from,
.som-tv-tile-leave-to {
  opacity: 0;
  transform: scale(0.92);
}

</style>
